/**
 * pdfDownload — cross-platform PDF "download / open" helper.
 *
 * iter134l — user reports the PDF button still silently no-ops on
 * Android AND iOS after iter134j (which added Filesystem+Share). The
 * root cause: the Capacitor native plugins require ``npx cap sync``
 * to register in the Android/iOS project, and even then Xiaomi/MIUI
 * strips background share intents on some builds. Any purely blob-
 * based approach is fragile inside a WebView.
 *
 * The new strategy skips the blob path on native entirely:
 *
 *   1. Callers pass ``(url, filename)`` — url is the backend PDF
 *      endpoint (e.g. ``/api/admin/commission-invoices/{id}/pdf``).
 *   2. We attach ``?token=<jwt>`` from tokenStore so the WebView-less
 *      OS browser / Custom Tab can authenticate the GET.
 *   3. On native: ``Browser.open({url})`` opens Chrome Custom Tabs
 *      (Android) / SafariViewController (iOS) — both know how to
 *      render + download a ``application/pdf`` response.
 *   4. Fallback: ``window.location.href = url`` navigates the
 *      Capacitor WebView to the PDF; modern Android WebView (chrome
 *      91+) renders PDFs inline.
 *   5. On web: still uses the blob → anchor download flow (kept for
 *      backward compat + progress bar in inspector).
 *
 * Callers using the old ``openOrDownloadPdf(blob, filename)`` API
 * still work — the blob path is used on web / desktop only.
 */
import { tokenStore } from "./tokenStore";

function _withToken(url) {
    // Append or replace a ``token`` query param on the URL. Preserves
    // any existing query params so callers can pass ?year=2026 etc.
    const tok = tokenStore.get();
    if (!tok) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}token=${encodeURIComponent(tok)}`;
}

/**
 * iter134l — preferred native path. Callers pass the PDF endpoint URL
 * (absolute or backend-relative) plus a filename hint for logging.
 * The helper attaches the JWT and delegates to Chrome Custom Tabs /
 * SafariViewController — both render + offer to save the PDF natively.
 */
export async function openPdfUrl(url, filename) {
    if (!url) throw new Error("openPdfUrl: missing url");
    const finalUrl = _withToken(url);

    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    const isNative = cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform();

    if (isNative) {
        // 1) @capacitor/browser — canonical way to open a URL in
        //    Custom Tabs / SafariViewController. The PDF renders in
        //    the OS-level browser with a native "Save / Share" UI.
        try {
            const mod = await import("@capacitor/browser");
            if (mod && mod.Browser && typeof mod.Browser.open === "function") {
                await mod.Browser.open({ url: finalUrl });
                return { via: "browser" };
            }
        } catch { /* fall through */ }

        // 2) @capacitor/app-launcher — falls back to the OS default
        //    handler for the URL (usually the browser).
        try {
            const mod = await import("@capacitor/app-launcher");
            if (mod && mod.AppLauncher && typeof mod.AppLauncher.openUrl === "function") {
                const r = await mod.AppLauncher.openUrl({ url: finalUrl });
                if (r && r.completed) return { via: "app-launcher" };
            }
        } catch { /* fall through */ }

        // 3) window.open with ``_system`` target — legacy Capacitor
        //    delegate-to-OS pattern. Some builds still honour this.
        try {
            const w = window.open(finalUrl, "_system");
            if (w) return { via: "system-window" };
        } catch { /* fall through */ }

        // 4) LAST RESORT — navigate the WebView itself to the PDF.
        //    Android WebView (Chrome-based, Android 10+) renders PDFs
        //    inline; iOS WKWebView shows a native PDF viewer. Not the
        //    prettiest UX (loses app context until the user hits back)
        //    but at least the PDF becomes visible to the user.
        try {
            window.location.href = finalUrl;
            return { via: "webview-nav" };
        } catch { /* give up */ }

        // 5) Everything failed — surface a toast so the tap isn't silent.
        try {
            const { toast } = await import("sonner");
            toast.error(
                "Couldn't open the PDF. Please update the app or install a browser.",
                { duration: 6000 },
            );
        } catch { /* nothing else */ }
        return { via: "failed" };
    }

    // Web / desktop — open in a new tab. Browser will render the PDF
    // inline (Chrome/Firefox/Safari all have native PDF viewers).
    try {
        window.open(finalUrl, "_blank", "noopener,noreferrer");
        return { via: "new-tab" };
    } catch {
        window.location.href = finalUrl;
        return { via: "same-tab" };
    }
}

// -----------------------------------------------------------------------------
// Legacy blob-based API — kept for web parity. On native it now delegates to
// openPdfUrl when the caller supplies a ``sourceUrl`` on the blob (see below).
// If the caller only has a raw blob without a source URL we still attempt the
// share-sheet path but this is a fragile last resort — new callers should
// prefer openPdfUrl instead.
// -----------------------------------------------------------------------------

async function _blobToBase64(blob) {
    return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => {
            const data = String(reader.result || "");
            const idx = data.indexOf(",");
            resolve(idx >= 0 ? data.slice(idx + 1) : data);
        };
        reader.readAsDataURL(blob);
    });
}

async function _capacitorNativeShare(blob, safeName) {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
        import("@capacitor/filesystem"),
        import("@capacitor/share"),
    ]);
    const base64 = await _blobToBase64(blob);
    const cleanName = safeName.replace(/[\\/:*?"<>|]+/g, "_");
    const written = await Filesystem.writeFile({
        path: cleanName,
        data: base64,
        directory: Directory.Cache,
        recursive: true,
    });
    await Share.share({
        title: cleanName,
        text: cleanName,
        url: written.uri,
        dialogTitle: "Save or open PDF",
    });
    return { via: "capacitor-share", uri: written.uri };
}

export async function openOrDownloadPdf(blob, filename, opts = {}) {
    if (!blob) throw new Error("openOrDownloadPdf: missing blob");
    const safeName = filename || "document.pdf";
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    const isNative = cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform();

    // iter134l — On native, prefer the URL path when the caller
    // provided a ``sourceUrl`` — Custom Tabs handle PDFs 100x more
    // reliably than the Filesystem+Share dance on Xiaomi / OnePlus /
    // MIUI builds where background intents get killed.
    if (isNative && opts.sourceUrl) {
        return openPdfUrl(opts.sourceUrl, safeName);
    }

    if (isNative) {
        try {
            return await _capacitorNativeShare(blob, safeName);
        } catch (e) {
            const s = String(e && e.message || e);
            if (/cancel|abort|dismiss/i.test(s) || (e && e.name === "AbortError")) {
                return { via: "capacitor-share-cancelled" };
            }
        }
    }

    // Web Share API — Chrome mobile web + iOS 16.4+.
    try {
        if (typeof File !== "undefined" && typeof navigator !== "undefined" && navigator.canShare) {
            const file = new File([blob], safeName, { type: "application/pdf" });
            if (navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title: safeName });
                return { via: "share" };
            }
        }
    } catch (e) {
        if (e && (e.name === "AbortError" || String(e).includes("cancel"))) {
            return { via: "share-cancelled" };
        }
    }

    const url = URL.createObjectURL(blob);

    const ua = navigator.userAgent || "";
    const isIOS = /iP(ad|hone|od)/.test(ua);
    if (isIOS) {
        const w = window.open(url, "_blank");
        if (!w) window.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        return { via: "ios-open" };
    }

    const a = document.createElement("a");
    a.href = url;
    a.download = safeName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return { via: "anchor" };
}

/** Pull ``filename="foo.pdf"`` out of a Content-Disposition header. */
export function filenameFromContentDisposition(cd, fallback) {
    if (!cd) return fallback;
    const m = /filename="?([^";]+)"?/i.exec(cd);
    return m ? m[1] : fallback;
}
